import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";

// Third-party imports
import { useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

// Utility imports
import { smoothScrollTo } from '../../utils/scrollUtils';

// Hooks
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { useTransition } from '../../context/TransitionContext';

// Navigation components
import MainNav from '../navigation/MainNav';
import MobileNav from '../navigation/MobileNav';

/**
 * Navigation item specification
 */
export interface NavItem {
  /** Path or URL route */
  href: string;
  /** Display label */
  label: string;
  /** Whether link is external */
  external?: boolean;
  /** Render as button element */
  isButton?: boolean;
  /** Click callback handler */
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  /** Optional icon component */
  icon?: React.ElementType;
}

/**
 * Header - Primary application header with sticky navigation and mobile drawer
 */
const Header: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
  const [scrolled, setScrolled] = useState<boolean>(false);
  const location = useLocation();
  const { navigateWithTransition } = useTransition();
  
  // Create a ref for the menu toggle button to restore focus when menu closes
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  
  // Track navigation clicks with useRef instead of global variable
  const isNavigatingRef = useRef<boolean>(false);
  
  // Use the scroll lock hook for mobile menu
  useBodyScrollLock(mobileMenuOpen);

  // Reusable navigation handler function - memoized to prevent recreating on every render
  const handleNavigation = useCallback((path: string, isHashNavigation: boolean = false) => {
    // Prevent multiple rapid clicks
    if (isNavigatingRef.current) return;
    isNavigatingRef.current = true;

    if (isHashNavigation) {
      if (location.pathname !== '/') {
        window.location.href = `/${path}`;
      } else {
        smoothScrollTo(path.replace('#', ''));
      }
    } else {
      navigateWithTransition(path);
    }

    // Reset the navigation lock after a delay
    setTimeout(() => {
      isNavigatingRef.current = false;
    }, 1000);
  }, [location.pathname, navigateWithTransition]);

  // Navigation items data - memoized to prevent recreating on every render
  const navItems = useMemo<NavItem[]>(() => [
    { 
      href: "/work", 
      label: "Work",
      onClick: (e) => {
        e.preventDefault();
        handleNavigation('/work');
      }
    },
    { 
      href: "/experience", 
      label: "Experience",
      onClick: (e) => {
        e.preventDefault();
        handleNavigation('/experience');
      }
    },
    { 
      href: "/about", 
      label: "About",
      onClick: (e) => {
        e.preventDefault();
        handleNavigation('/about');
      }
    },
    // New contact button item with isButton flag
    {
      href: "/contact",
      label: "Contact",
      isButton: true, // Flag to style this as a button
      onClick: (e) => {
        e.preventDefault();
        handleNavigation('/contact');
      }
    }
  ], [handleNavigation]);

  // Add scroll event listener to change header appearance on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    // Initial check
    handleScroll();
    
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Handle hash navigation when arriving at homepage
  useEffect(() => {
    if (location.pathname === '/') {
      const hash = window.location.hash.slice(1); // Remove the # symbol
      if (hash) {
        // Add a delay to ensure the page content is loaded
        const timer = setTimeout(() => {
          smoothScrollTo(hash);
        }, 500);
        return () => clearTimeout(timer);
      }
    }
    return undefined;
  }, [location.pathname]);

  const toggleMobileMenu = useCallback(() => {
    setMobileMenuOpen((prevState) => !prevState);
  }, []);

  const handleLogoClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (location.pathname !== '/') {
      handleNavigation('/');
    } else {
      // If already on homepage, just scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [location.pathname, handleNavigation]);

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? 'bg-background/90 backdrop-blur-md' : 'bg-transparent'
      }`}
      style={{
        // Ensure proper stacking over mobile menu
        zIndex: mobileMenuOpen ? 50 : 30,
      }}
    >
      <div className="max-w-screen-2xl mx-auto py-4 px-5 md:px-12 lg:px-16 flex justify-between items-center">
        {/* Logo - Always visible */}
        <div className="flex items-center z-50" style={{ position: 'relative' }}>
          <a 
            href="/" 
            className="flex items-center" 
            onClick={handleLogoClick}
            aria-label="Shaun Scott homepage"
          >
            <img
              src={`${import.meta.env.BASE_URL}assets/images/logo/sscottlogo.png`}
              alt="Shaun Scott Logo"
              className="h-10 w-auto"
            />
          </a>
        </div>

        {/* Desktop Navigation */}
        <MainNav navItems={navItems} />

        {/* Mobile Menu Toggle Button */}
        <button
          ref={menuButtonRef}
          className="md:hidden text-accent hover:text-white focus:outline-none focus:ring-2 focus:ring-accent focus:ring-opacity-50 rounded-md p-1 transition-all duration-300"
          onClick={toggleMobileMenu}
          aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileMenuOpen}
          aria-controls="mobile-menu"
          style={{
            position: 'relative',
            zIndex: 50,
            transform: mobileMenuOpen ? 'rotate(90deg) scale(1.1)' : 'rotate(0deg) scale(1)',
          }}
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Navigation - Now with enhanced accessibility and focus trapping */}
      <MobileNav
        isOpen={mobileMenuOpen}
        navItems={navItems}
        onLinkClick={() => setMobileMenuOpen(false)}
        menuButtonRef={menuButtonRef}
      />
    </header>
  );
};

export default Header;

